import {
  ref, watch, computed, onBeforeMount,
} from '@vue/composition-api'
import { useRouter, onlyWithValue } from '@core/utils/utils'
// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import i18n from '@/libs/i18n'

export default function tableConfig(moduleName, arg = {}, filterClass) {
  // Use toast
  const defaultParams = {
    limit: 10,
    search_query: '',
    order_column: 'id',
    order_type: 'desc',
    page: 1,
  }

  const defaultQuery = {
    ordering: (defaultParams.order_type === 'desc' ? '-' : '') + defaultParams.order_column,
    offset: (defaultParams.page - 1) * defaultParams.limit,
    limit: defaultParams.limit,
  }

  const toast = useToast()
  const table = ref(null)
  const perPage = ref(arg.perPage ?? defaultParams.limit)
  const total = ref(arg.total ?? 0)
  const currentPage = ref(arg.currentPage ?? defaultParams.page)
  const perPageOptions = arg.perPageOptions ?? [5, 10, 25, 50, 100]
  const searchQuery = ref(arg.searchQuery ?? defaultParams.search_query)
  const sortBy = ref(arg.sortBy ?? defaultParams.order_column)
  const tableListArray = ref([])
  const isSortDirDesc = ref(arg.isSortDirDesc ?? true)
  const isBusy = ref(arg.isBusy ?? false)
  const extraParams = ref(arg.data ?? {})
  const endpoint = arg.endpoint ?? `${moduleName}/list`
  const dataMeta = computed(() => {
    const localItemsCount = table.value ? table.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: total.value,
    }
  })
  const isPagination = ref(false)
  const customeRoute = useRouter()
  const filters = ref(customeRoute.route.value.query)
  const selectedFilters = ref([])
  const refetchData = () => {
    table.value.refresh()
  }
  const ignoreDefaultParams = (currentParamsObject, defaultParamsObject) => {
    const newParamsObject = JSON.parse(JSON.stringify(currentParamsObject))
    Object.entries(defaultParamsObject).forEach(([itemKey, itemValue]) => {
      if (currentParamsObject[itemKey] === itemValue) {
        delete newParamsObject[itemKey]
      }
    })
    return newParamsObject
  }

  const getBody = response => {
    const body = response.data
    let data
    let totalValue = 0
    let isPaginationValue = false

    // eslint-disable-next-line no-prototype-builtins
    if (body.hasOwnProperty('next') && body.hasOwnProperty('previous') && body.hasOwnProperty('count')) {
      isPaginationValue = true
      data = body.results
      totalValue = body.count
    } else {
      data = body
    }
    return {
      data,
      totalValue,
      isPaginationValue,
    }
  }

  onBeforeMount(() => {
    const query = arg.syncRoute === false ? {} : customeRoute.route.value.query
    perPage.value = query.limit || arg.perPageOptions || defaultParams.limit

    let page = arg.currentPage || defaultParams.page
    // eslint-disable-next-line no-prototype-builtins
    if (query.hasOwnProperty('offset')) {
      // eslint-disable-next-line radix
      page = parseInt(query.offset / perPage.value) + 1
    }
    currentPage.value = Number(page)
    let orderColumn = arg.sortBy || defaultParams.order_column
    let orderType = arg.isSortDirDesc || true

    // eslint-disable-next-line no-prototype-builtins
    if (query.hasOwnProperty('ordering')) {
      orderType = query.ordering[0] === '-'
      orderColumn = query.ordering.substr(Number(orderType))
    }
    sortBy.value = orderColumn
    searchQuery.value = query.search || arg.searchQuery || defaultParams.search_query
    isSortDirDesc.value = orderType
  })

  watch([currentPage, perPage], () => {
    refetchData()
  })
  const list = (ctx, callback) => {
    isBusy.value = true
    let query = arg.syncRoute === false ? {} : customeRoute.route.value.query
    // eslint-disable-next-line no-shadow
    if (filterClass) {
      filters.value = {
        ...filterClass.struct(),
        ...filters.value,
      }
      const filterMapping = filterClass.convertToQuery(filters.value)

      query = {
        ...query,
        ...filterMapping,
      }
    }
    const totalQuery = onlyWithValue({
      ...query,
      search: searchQuery.value,
      ordering: (isSortDirDesc.value ? '-' : '') + sortBy.value,
      limit: perPage.value,
      offset: (currentPage.value - 1) * perPage.value,
    })
    store
      .dispatch(endpoint, { ...totalQuery, ...extraParams.value })
      .then(response => {
        if (arg.syncRoute !== false) {
          customeRoute.router.replace({
            query: ignoreDefaultParams(totalQuery, defaultQuery),
          }).catch(() => {})
        }
        // eslint-disable-next-line prefer-const
        let { data, isPaginationValue, totalValue } = getBody(response)

        if (Array.isArray(data)) {
          tableListArray.value = data
        }

        isPagination.value = isPaginationValue
        total.value = totalValue
        if (typeof arg.getData === 'function') {
          data = arg.getData(response)
        }
        callback(data)
        if (typeof arg.getTotal === 'function') {
          total.value = arg.getTotal(response)
        }
      })
      .catch(err => {
        console.log(err)
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('Error fetching {module} list', { module: moduleName }),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        isBusy.value = false
      })
  }

  return {
    list,
    perPage,
    currentPage,
    total,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    [`${moduleName}Table`]: table,
    isBusy,
    refetchData,
    extraParams,
    isPagination,
    endpoint,
    getBody,
    filters,
    selectedFilters,
    tableListArray,
  }
}
